<script setup>
/**
 * One notification, on both surfaces that show them: the bell dropdown and the
 * /notifications overview. One component rather than two, because the severity
 * palette, the paragraph rendering and the "Discuss this" hand-off must be
 * identical wherever a notice is read — see alert-presentation.js.
 *
 * The two surfaces differ only in `expandable`:
 *
 *   • dropdown (`expandable` = true): the body is clamped to three lines and the
 *     readable part of the row is a real <button> that expands it in place. A
 *     notice you cannot finish reading without leaving the dropdown is a notice
 *     most people will not finish reading.
 *   • overview (`expandable` = false): every body is already open, so there is
 *     nothing to click and the row must NOT pretend to be a control.
 *
 * WHY THE ACTIONS SIT OUTSIDE THE EXPAND BUTTON
 * ---------------------------------------------
 * "Discuss this" is a <button> and the action is an <a>/<router-link>. Nesting
 * either inside the expand <button> is invalid HTML, and browsers recover from
 * it by hoisting the inner control out of the button — which quietly breaks
 * both. So they are siblings in the row's own stacking order and can never
 * trigger an expand. They also carry `.stop`, which is belt-and-braces for the
 * day someone adds a click handler to the row itself.
 */
import { computed } from 'vue'
import { mdiClose, mdiOpenInNew, mdiMessageText, mdiChevronDown } from '@mdi/js'
import Icon from '@/components/Icon.vue'
import { formatRelativeTime, formatLocalDateTime } from '@/utils/datetime'
import { toneFor, toParagraphs, toPreviewText } from '@/components/alerts/alert-presentation'

const props = defineProps({
  alert: { type: Object, required: true },
  /** Is the full body showing? Owned by the parent, so only one is open. */
  expanded: { type: Boolean, default: false },
  /** False on the overview, where every body is open and nothing is clickable. */
  expandable: { type: Boolean, default: true },
  /** The overview also prints the exact timestamp; the dropdown keeps it to a
      title attribute, where it costs no vertical space. */
  showAbsoluteTime: { type: Boolean, default: false },
})

const emit = defineEmits(['dismiss', 'discuss', 'seen', 'navigate', 'toggle'])

const tone = computed(() => toneFor(props.alert.severity))

const isUnread = computed(() => !props.alert.readAt)

/** Open whenever the parent says so, and always open when not expandable. */
const isOpen = computed(() => !props.expandable || props.expanded)

/* Paragraphs when open, one run-on line when clamped. Both come from the shared
   splitter, so a blank line means the same thing on either surface. */
const paragraphs = computed(() => toParagraphs(props.alert.body))
const previewText = computed(() => toPreviewText(props.alert.body))

const hasBody = computed(() => paragraphs.value.length > 0)

/** Referenced by the trigger's `aria-controls`, so the relationship between the
    button and the region it opens is announced rather than implied. */
const bodyId = computed(() => `alert-body-${props.alert.id}`)

const relativeTime = computed(() => formatRelativeTime(props.alert.createdAt))

const exactTime = computed(() =>
  props.alert.createdAt ? formatLocalDateTime(props.alert.createdAt) : ''
)

/*
 * The router runs on hash history, so a panel link arrives as '#/orders' (or
 * '#/campaigns'). Those must be router-links: a plain <a href="#/campaigns"> inside a
 * teleported dropdown does navigate, but it does so without the router's guards
 * and it reloads nothing, so the dropdown is left hanging open over the new page.
 *
 * Anything else — a docs page, a Stripe receipt — is a real external link and
 * gets target="_blank" plus rel="noopener", never a router-link that would try
 * to resolve "https://..." as a route and land on the 404 view.
 */
const isInternalAction = computed(() => {
  const url = props.alert.actionUrl
  if (!url) return false
  return url.startsWith('#/') || (url.startsWith('/') && !url.startsWith('//'))
})

const internalTarget = computed(() =>
  props.alert.actionUrl.startsWith('#') ? props.alert.actionUrl.slice(1) : props.alert.actionUrl
)

const hasAction = computed(() => Boolean(props.alert.actionUrl && props.alert.actionLabel))

/* Any deliberate interaction with the row counts as "seen". Merely having been
   fetched does not — that is the poller's business, not the reader's. */
const markSeen = () => emit('seen', props.alert.id)

/**
 * Expanding is about as unambiguous a "seen" as exists — the reader asked for
 * the rest of the text — so it marks read on the way. Collapsing does not
 * un-read it, which would be nonsense.
 */
const onTriggerClick = () => {
  if (!props.expandable) return
  if (!props.expanded) markSeen()
  emit('toggle', props.alert.id)
}

const onActionClick = () => {
  markSeen()
  emit('navigate')
}
</script>

<template>
  <li
    :data-alert-id="alert.id"
    class="relative"
    :class="isUnread ? tone.unread : ''"
    @mouseenter="markSeen"
    @focusin="markSeen"
  >
    <div class="flex items-start gap-2 px-3 py-3 sm:px-4">
      <!--
        The readable region. A real <button> when it expands, so Enter and Space
        work with no keyboard handlers of our own, and a plain <div> on the
        overview — a control that does nothing when pressed is worse than no
        control, and `role="button"` on something inert is worse still.
      -->
      <component
        :is="expandable ? 'button' : 'div'"
        :type="expandable ? 'button' : null"
        :aria-expanded="expandable ? String(expanded) : null"
        :aria-controls="expandable && hasBody ? bodyId : null"
        class="flex min-w-0 flex-1 items-start gap-3 rounded-lg text-left focus:outline-none"
        :class="expandable
          ? 'cursor-pointer focus-visible:ring-2 focus-visible:ring-brand-500'
          : ''"
        @click="onTriggerClick"
      >
        <!-- Severity tile. The word is for screen readers; sighted readers get
             it from the glyph's shape and colour together. -->
        <span
          class="mt-0.5 inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full"
          :class="tone.tile"
        >
          <icon :path="tone.icon" :size="18" w="w-5" h="h-5" />
        </span>

        <div class="min-w-0 flex-1">
          <p
            class="flex items-start gap-1 text-sm font-semibold leading-snug text-gray-900 break-words dark:text-gray-100"
          >
            <span class="sr-only">{{ tone.label }}:</span>
            <span class="min-w-0 flex-1">{{ alert.title }}</span>
            <!-- The affordance. Without it a clamped body looks like the whole
                 notice and nobody discovers there is more. -->
            <icon
              v-if="expandable && hasBody"
              :path="mdiChevronDown"
              :size="16"
              w="w-4"
              h="h-4"
              class="mt-0.5 flex-shrink-0 text-gray-400 transition-transform duration-200 dark:text-gray-500"
              :class="expanded ? 'rotate-180' : ''"
            />
          </p>

          <!--
            Collapsed: the whole body as one clamped run, so all three lines
            carry text. `line-clamp-3` is Tailwind v3 and emits nothing on 2.2,
            so the clamp is written out in the scoped block below.
          -->
          <p
            v-if="hasBody && !isOpen"
            class="notification-body mt-0.5 text-sm leading-snug text-gray-600 dark:text-gray-300"
          >
            {{ previewText }}
          </p>

          <!--
            Expanded: real paragraphs. A blank line in a staff note separates
            "what we changed" from "what you need to do", and running the two
            together is how the second half stops being read.

            Each paragraph is a TEXT NODE, never v-html — this text is written by
            staff and stored in a database, so interpolating it as markup would
            turn the support tool into a stored-XSS vector. `whitespace-pre-line`
            keeps single newlines inside a paragraph as line breaks.
          -->
          <div v-else-if="hasBody" :id="bodyId" class="mt-0.5">
            <p
              v-for="(paragraph, index) in paragraphs"
              :key="index"
              class="notification-paragraph text-sm leading-relaxed text-gray-600 whitespace-pre-line dark:text-gray-300"
              :class="index > 0 ? 'mt-2' : ''"
            >
              {{ paragraph }}
            </p>
          </div>

          <!-- The job this is about. A staff note that changed something on one
               order is unreadable without saying which one. -->
          <p
            v-if="alert.orderLabel"
            class="mt-1.5 inline-flex max-w-full items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 break-all dark:bg-gray-700 dark:text-gray-300"
          >
            <span class="notranslate" translate="no">{{ alert.orderLabel }}</span>
          </p>

          <p
            v-if="relativeTime"
            class="mt-1.5 text-xs text-gray-400 dark:text-gray-500"
            :title="exactTime"
          >
            <span
              v-if="isUnread"
              class="mr-1.5 inline-block h-1.5 w-1.5 rounded-full bg-brand-600 align-middle dark:bg-brand-400"
              aria-hidden="true"
            />
            {{ relativeTime }}
            <!-- The overview prints the exact instant too. On a page you opened
                 to read everything, "about 1 month ago" is not enough to tell
                 two notices apart. -->
            <span v-if="showAbsoluteTime && exactTime" class="break-words">
              · <span class="notranslate" translate="no">{{ exactTime }}</span>
            </span>
            <span v-if="isUnread" class="sr-only">— unread</span>
          </p>
        </div>
      </component>

      <button
        type="button"
        class="-mr-1 inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 sm:h-8 sm:w-8 dark:hover:bg-gray-700 dark:hover:text-gray-100"
        :aria-label="`Dismiss notification: ${alert.title}`"
        @click.stop="emit('dismiss', alert)"
      >
        <icon :path="mdiClose" :size="16" w="w-4" h="h-4" />
      </button>
    </div>

    <!--
      Actions, indented to sit under the text rather than under the icon. Below
      the expand button, never inside it — see the header note.
    -->
    <div
      v-if="hasAction || alert.allowChat"
      class="flex flex-wrap items-center gap-2 pb-3 pl-14 pr-3 sm:pl-[3.75rem] sm:pr-4"
    >
      <router-link
        v-if="hasAction && isInternalAction"
        :to="internalTarget"
        class="inline-flex min-h-[2.25rem] items-center rounded-lg bg-brand-600 px-3 text-xs font-semibold text-white transition-colors hover:bg-brand-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-800"
        @click.stop="onActionClick"
      >
        {{ alert.actionLabel }}
      </router-link>
      <a
        v-else-if="hasAction"
        :href="alert.actionUrl"
        target="_blank"
        rel="noopener noreferrer"
        class="inline-flex min-h-[2.25rem] max-w-full items-center gap-1 rounded-lg bg-brand-600 px-3 text-xs font-semibold text-white break-all transition-colors hover:bg-brand-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-800"
        @click.stop="markSeen"
      >
        {{ alert.actionLabel }}
        <icon :path="mdiOpenInNew" :size="13" w="w-3" h="h-3" />
      </a>

      <button
        v-if="alert.allowChat"
        type="button"
        class="inline-flex min-h-[2.25rem] items-center gap-1 rounded-lg border border-gray-200 bg-white px-3 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700 dark:focus-visible:ring-offset-gray-800"
        @click.stop="emit('discuss', alert)"
      >
        <icon :path="mdiMessageText" :size="13" w="w-3" h="h-3" />
        Discuss this
      </button>
    </div>
  </li>
</template>

<style scoped>
/*
 * Three lines, then an ellipsis. `-webkit-line-clamp` is the only mechanism with
 * real cross-browser support for this, and Tailwind 2.2 has no utility for it
 * (`line-clamp-*` arrived in v3 and emits nothing here), so it is written out.
 *
 * `overflow-wrap: anywhere` is the mobile half of the rule, and it is on the
 * expanded paragraphs too: a staff note can contain a bare URL, and at 375px an
 * unbroken 60-character link would push the whole dropdown sideways. Clamping
 * the height does nothing about width.
 */
.notification-body {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.notification-paragraph {
  overflow-wrap: anywhere;
  word-break: break-word;
}
</style>
